/**
 * Remove FHIR-disallowed control bytes from a string.
 *
 * FHIR R4 `string` is constrained by the regex `[ \r\n\t\S]+`, which excludes
 * the C0 control bytes other than `\t` (0x09), `\n` (0x0A), and `\r` (0x0D).
 * Medplum's server-side validator enforces this with
 * `OperationOutcomeError: Invalid string format`. Two upstream sources have
 * been observed producing these bytes:
 *
 * 1. Elation API responses with corrupted curly punctuation (e.g. `U+2019` →
 *    `0x19`).
 * 2. Gemini AI OCR output of scanned PDFs.
 *
 * The disallowed bytes are: `0x00–0x08`, `0x0B`, `0x0C`, `0x0E–0x1F`. The DEL
 * character (`0x7F`) is permitted by the FHIR spec (it falls into `\S`) and
 * is therefore preserved. This util is intentionally a thin regex strip so
 * the behavior matches the FHIR spec exactly — no over-stripping of
 * permitted whitespace (`\t\n\r`) or DEL.
 */
// oxlint-disable-next-line no-control-regex -- matching disallowed FHIR control bytes is the entire purpose of this regex
const FHIR_INVALID_CONTROL_BYTES = /[\x00-\x08\x0B\x0C\x0E-\x1F]/g

export function sanitizeFhirString<T extends string | null | undefined>(
  value: T
): T {
  if (typeof value !== 'string') {
    return value
  }
  return value.replace(FHIR_INVALID_CONTROL_BYTES, '') as T
}
